import type { FocusEvent } from 'react';
import { useStore } from '@/lib/store';
import { dismissToast, holdToast, releaseToast, toastStore, type ToastItem } from '@/lib/toast';
import { Icon } from './Icon';

/** Shows the toast stack at the bottom centre of the screen. */
export function Toasts() {
  const { items, raised } = useStore(toastStore, (s) => s);
  return (
    <div className={raised ? 'toasts raised' : 'toasts'} aria-live="polite">
      {items.map((t) => (
        <div
          key={t.id}
          className={['toast', t.leaving ? 'out' : '', t.error ? 'err' : ''].filter(Boolean).join(' ')}
          role={t.error ? 'alert' : 'status'}
          {...holdProps(t)}
        >
          <Icon name={t.icon ?? 'check-circle'} />
          <span className="t-msg">{t.message}</span>
          {t.undo ? (
            <button
              type="button"
              className="t-act"
              onClick={() => {
                t.undo?.();
                dismissToast(t.id);
              }}
            >
              Undo
            </button>
          ) : null}
          {t.action ? (
            <button
              type="button"
              className="t-act"
              onClick={() => {
                t.action?.run();
                dismissToast(t.id);
              }}
            >
              {t.action.label}
            </button>
          ) : null}
          <button type="button" className="t-x" aria-label="Dismiss" onClick={() => dismissToast(t.id)}>
            <Icon name="x" />
          </button>
        </div>
      ))}
    </div>
  );
}

/** A toast with Undo or another action waits while the pointer is on it or focus is inside it. */
function holdProps(t: ToastItem) {
  if (!t.undo && !t.action) return {};
  return {
    onMouseEnter: () => holdToast(t.id, 'hover'),
    onMouseLeave: () => releaseToast(t.id, 'hover'),
    onFocus: () => holdToast(t.id, 'focus'),
    onBlur: (e: FocusEvent<HTMLDivElement>) => {
      if (!e.currentTarget.contains(e.relatedTarget)) releaseToast(t.id, 'focus');
    },
  };
}
